import { ArrowRight, Asterisk } from 'lucide-react';
import { AnchorLink, Eyebrow, Reveal } from './Reveal';

const STEPS = [
  {
    testid: 'approach-step-1',
    tag: 'STEP 01',
    title: 'Tell us what you need',
    desc: 'Share roles, quantity, location, timing and business expectations.',
  },
  {
    testid: 'approach-step-2',
    tag: 'STEP 02',
    title: 'Choose the right model',
    desc: 'We discuss the appropriate recruitment, staffing or payroll solution.',
  },
  {
    testid: 'approach-step-3',
    tag: 'STEP 03',
    title: 'Coordinate execution',
    desc: 'Candidate sourcing, deployment, onboarding or payroll setup as agreed.',
  },
  {
    testid: 'approach-step-4',
    tag: 'STEP 04',
    title: 'Stay supported',
    desc: 'Follow-up and ongoing coordination within the agreed service scope.',
  },
];

export default function Approach() {
  return (
    <>
      <section id="our-approach" data-testid="approach-section" className="scroll-mt-28 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Reveal>
              <Eyebrow index="09">Our approach</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                Clear process. <span className="text-royal">Practical execution.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                Every engagement starts by understanding your requirement—not by offering a one-size-fits-all
                package.
              </p>
            </Reveal>
          </div>

          <div className="relative mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div aria-hidden="true" className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-royal/25 to-transparent lg:block" />
            {STEPS.map((s, i) => (
              <Reveal key={s.testid} delay={i * 0.1} className="h-full">
                <article
                  data-testid={s.testid}
                  className="group relative h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-card transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-royal/30 hover:shadow-lift"
                >
                  <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-royal font-display text-base font-extrabold text-white shadow-[0_10px_22px_-8px_rgba(45,58,151,0.6)] transition-transform duration-300 group-hover:scale-105">
                    0{i + 1}
                    <span aria-hidden="true" className="absolute -right-1.5 -top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-lime" />
                  </span>
                  <p className="mt-5 font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">{s.tag}</p>
                  <h3 className="mt-2 font-display text-lg font-extrabold text-ink">{s.title}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{s.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="for-job-seekers" data-testid="jobseekers-section" className="scroll-mt-28 px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <Reveal>
          <div className="grain-dark relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-lime px-6 py-14 sm:px-12 lg:px-16 lg:py-20">
            <div aria-hidden="true" className="pointer-events-none absolute -left-16 -top-24 h-72 w-72 rounded-full bg-white/40 blur-3xl" />
            <Asterisk aria-hidden="true" className="pointer-events-none absolute -right-4 bottom-[-3rem] hidden h-56 w-56 text-white/30 lg:block" strokeWidth={1.2} />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-royal-ink sm:text-4xl">
                For job seekers
              </h2>
              <p className="mt-3 font-display text-xl font-bold italic text-royal-ink/80">
                Looking for your next opportunity?
              </p>
              <p className="mt-4 text-base leading-relaxed text-royal-ink/75">
                Connect with our recruitment team to enquire about suitable openings. Registration does not guarantee
                placement.
              </p>
              <a
                href="mailto:connect@saniajobbowl.com?subject=Candidate%20Registration%20Enquiry"
                data-testid="jobseekers-cta"
                className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-royal-ink px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-royal hover:shadow-[0_18px_36px_-14px_rgba(12,20,64,0.6)]"
              >
                Contact Our Recruitment Team
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
